import { motion } from 'framer-motion'
import { AlertCircle } from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formStyles } from '@/constants/styles'
import { AssetsInputWithTags } from './AssetsInputWithTags'
import { INDUSTRIES_OF_INTEREST, INVESTMENT_AMOUNT_OPTIONS, POPULAR_ASSETS_BUY, POPULAR_ASSETS_SELL } from './ProfilePreferencesScreenBase'
import type { ProfilePreferencesScreenBaseProps } from './ProfilePreferencesScreenBase'

// Background images from Figma
const imgGroup691314559 = "https://www.figma.com/api/mcp/asset/23e406db-4ebc-454e-87f8-c4c79bca00f2"

export const ProfilePreferencesScreenMobile = ({
  formData,
  loading,
  error,
  onFormDataChange,
  onIndustryToggle,
  onSelectAllIndustries,
  onClearAllIndustries,
  onSubmit,
}: ProfilePreferencesScreenBaseProps) => {
  const handleInputChange = (field: keyof typeof formData, value: string) => {
    onFormDataChange({ [field]: value })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gradient-to-b from-[#f2f1f6] to-[#eae9ec] relative min-h-screen py-10 px-4 flex flex-col items-center"
    >
      {/* Title and Subtitle */}
      <div className="flex flex-col gap-2 items-center text-[#2d2923] text-center w-full mb-8">
        <h1 className="font-plus-jakarta font-medium leading-[1.2] text-[28px] tracking-[-0.52px]">
          Account Verification Required
        </h1>
        <p className="font-plus-jakarta font-normal leading-[24px] text-[14px] opacity-80">
          Please complete the following attestation to access your account
        </p>
      </div>

      {/* Main Card */}
      <div className="bg-white border-2 border-solid border-white w-full rounded-xl shadow-lg overflow-hidden relative">
        {/* Background image */}
        <div className="absolute bottom-[-100px] right-[-100px] h-[300px] w-[300px] pointer-events-none opacity-10">
          <img 
            alt="" 
            className="w-full h-full object-cover" 
            src={imgGroup691314559} 
          />
        </div>

        <form onSubmit={onSubmit} className="relative z-10 p-6 space-y-8">
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 flex items-center gap-2 rounded text-sm">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Profile Information Section */}
          <section className="space-y-4">
            <h2 className="font-plus-jakarta font-medium text-[18px] text-[#2d2923] leading-[24px]">
              Profile Information
            </h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2d2923]">First name</label>
                <Input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  placeholder="First name"
                  className={formStyles.input}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2d2923]">Last Name</label>
                <Input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  placeholder="Last name"
                  className={formStyles.input}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2d2923]">Job Title</label>
                <Input
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => handleInputChange('job_title', e.target.value)}
                  placeholder="Job title"
                  className={formStyles.input}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2d2923]">Company Name</label>
                <Input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Company name"
                  className={formStyles.input}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2d2923]">Country</label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => handleInputChange('country', value)}
                >
                  <SelectTrigger className="w-full !bg-white !border-[rgba(45,41,35,0.12)]">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="!bg-white">
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="Germany">Germany</SelectItem>
                    <SelectItem value="France">France</SelectItem>
                    <SelectItem value="Singapore">Singapore</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Interests Section */}
          <section className="space-y-6">
            <AssetsInputWithTags
              label="Assets you're interested in buying"
              value={formData.companies_of_interest_buy}
              onChange={(assets) => onFormDataChange({ companies_of_interest_buy: assets })}
              placeholder="e.g., Ethereum, Solana"
              quickOptions={POPULAR_ASSETS_BUY}
            />

            <AssetsInputWithTags
              label="Assets you're planning to sell"
              value={formData.companies_of_interest_sell}
              onChange={(assets) => onFormDataChange({ companies_of_interest_sell: assets })}
              placeholder="e.g., Bitcoin, Cardano"
              quickOptions={POPULAR_ASSETS_SELL}
            />

            <div className="space-y-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[#2d2923]">Categories</label>
                <p className="text-xs opacity-70">Let us know to unlock relevant resources</p>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={onSelectAllIndustries} className="text-xs underline">Select All</button>
                <button type="button" onClick={onClearAllIndustries} className="text-xs underline">Clear All</button>
              </div>
              <div className="bg-gray-50 border border-[rgba(45,41,35,0.12)] p-3 rounded-lg flex flex-wrap gap-2">
                {INDUSTRIES_OF_INTEREST.map((industry) => (
                  <button
                    key={industry}
                    type="button"
                    onClick={() => onIndustryToggle(industry)}
                    className={`px-2 py-1 rounded-full text-xs transition-all border ${
                      formData.industries_of_interest.includes(industry)
                        ? 'bg-[#2d2923] text-white border-[#2d2923]'
                        : 'bg-white text-[#2d2923] border-[rgba(45,41,35,0.12)]'
                    }`}
                  >
                    {industry}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Investment Preferences Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-medium text-[#2d2923]">Investment Amount</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Minimum amount</label>
                <Select
                  value={formData.preferred_investment_amount_min?.toString() || ''}
                  onValueChange={(value) => onFormDataChange({ 
                    preferred_investment_amount_min: value ? parseInt(value) : undefined 
                  })}
                >
                  <SelectTrigger className="w-full !bg-white">
                    <SelectValue placeholder="Select min" />
                  </SelectTrigger>
                  <SelectContent className="!bg-white">
                    {INVESTMENT_AMOUNT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value.toString()}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Maximum amount</label>
                <Select
                  value={formData.preferred_investment_amount_max?.toString() || ''}
                  onValueChange={(value) => onFormDataChange({ 
                    preferred_investment_amount_max: value ? parseInt(value) : undefined 
                  })}
                >
                  <SelectTrigger className="w-full !bg-white">
                    <SelectValue placeholder="Select max" />
                  </SelectTrigger>
                  <SelectContent className="!bg-white">
                    {INVESTMENT_AMOUNT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value.toString()}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="pt-4 space-y-4">
            <Button
              type="submit"
              variant="primary"
              className="w-full h-12 bg-[#2d2923] hover:bg-[#1a1612] text-white rounded-lg"
              isLoading={loading}
            >
              SUBMIT PREFERENCES
            </Button>
            <p className="text-xs text-[rgba(45,41,35,0.72)] text-center">
              You can always update these preferences later in your profile settings.
            </p>
          </div>
        </form>
      </div>
    </motion.div>
  )
}
